import {
  ArrowRightLeft,
  BadgePercent,
  BookMarked,
  BookOpen,
  BookOpenText,
  Calendar,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  FileQuestion,
  LayoutDashboard,
  LogOut,
  NotebookText,
  School,
  Settings,
  Timer,
  User2,
  Users2,
  Wallet2,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { useLanguage } from "../context/LanguageContext";
import { SidebarData } from "../lib/languages";
import { useAuth } from "../context/AuthContext";

const LogoSideBar = ({ logoName }) => (
  <SidebarMenu>
    <SidebarMenuItem>
      <SidebarMenuButton
        size="lg"
        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
      >
        <Link to={"/teacher/courses"} className="flex items-center gap-2">
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <BookOpen className="size-4" />
          </div>
          <span className="truncate font-semibold">{logoName}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  </SidebarMenu>
);

export function AppSidebarTeacher() {
  const { logout } = useAuth();
  const { language } = useLanguage();
  const t = SidebarData[language] || SidebarData["en"];

  return (
    <Sidebar
      variant={"floating"}
      collapsible={"icon"}
      side={language === "ar" ? "right" : "left"}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <SidebarHeader>
        <LogoSideBar logoName={t.logoName} />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <MenuItemComponent
                Icon={NotebookText}
                url={"/teacher/courses"}
                title={"Courses"}
              />
              <MenuItemComponent
                Icon={Timer}
                url={"/teacher/availability"}
                title={"Time Availability"}
              />
              <MenuItemComponent
                Icon={DollarSign}
                url={"/teacher/prices"}
                title={"Prices"}
              />
              <MenuItemComponent
                Icon={Wallet2}
                url={"/teacher/salary"}
                title={"Salary"}
              />
              <MenuItemComponent
                Icon={ArrowRightLeft}
                url={"/teacher/transactions"}
                title={"Transactions"}
              />
              <MenuItemComponent
                Icon={School}
                url={"/teacher/assignments"}
                title={"Home Works"}
              />
              <MenuItemComponent
                Icon={FileQuestion}
                url={"/teacher/quizez"}
                title={"Quizez"}
              />
              <MenuItemComponent
                Icon={Users2}
                url={"/teacher/students"}
                title={"Students"}
              />
              <MenuItemComponent
                Icon={BookOpenText}
                url={"/teacher/gradebook"}
                title={"Gradebook"}
              />
              <MenuItemComponent
                Icon={BadgePercent}
                url={"/teacher/promo-code"}
                title={"Promo Code"}
              />
              <MenuItemComponent
                Icon={Settings}
                url={"/teacher/teacher-settings"}
                title={"Setting"}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={"Logout"}
              className="bg-blue-600 text-white transition-colors hover:bg-blue-600/80 hover:text-white h-10"
              onClick={logout}
            >
              <Link to={"/"}>
                <LogOut />
                <span>{"Logout"}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

const MenuItemComponent = ({ title, url, Icon }) => (
  <SidebarMenuItem>
    <SidebarMenuButton asChild tooltip={title}>
      <NavLink to={url}>
        <Icon />
        <span>{title}</span>
      </NavLink>
    </SidebarMenuButton>
  </SidebarMenuItem>
);

const CollapsComponent = ({ Icon, title, items }) => {
  const { language } = useLanguage();
  const ArrowIcon = language === "ar" ? ChevronLeft : ChevronRight;

  return (
    <Collapsible asChild className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={title}>
            {Icon && <Icon />}
            <span>{title}</span>
            <ArrowIcon
              className={`${
                language === "ar"
                  ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                  : "ml-auto group-data-[state=open]/collapsible:rotate-90"
              } transition-transform duration-200`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarMenuSub>
            {items.map((item) => (
              <SidebarMenuSubItem key={item.title} tooltip={item.title}>
                <SidebarMenuSubButton asChild>
                  <Link to={item.url}>
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};
